import "normalize.css";
import "./styles/global.scss";

import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, HashRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import App from "./app/App";
import AuthPopup from "./app/AuthPopup";
import { isAuthPopup } from "./shared/auth-frame";
import { AuthProvider } from "./shared/auth";
import { startPwaUpdates } from "./shared/pwa-update";
import { startMonitoring } from "./shared/monitoring";
import { startUiFeedback } from "./shared/ui-feedback";
import { startAppViewport } from "./shared/app-shell";
import { installCanvasPolyfills } from "./shared/canvas-polyfills";
import { ON_PORTAL } from "./shared/portal-env";
import { startPortal, portalLoaded } from "./shared/portal";

// Error reports (Sentry): off unless the build has a DSN, see shared/monitoring.ts.
startMonitoring();
installCanvasPolyfills(); // roundRect and conic gradients for older browsers
startUiFeedback(); // a click sound on every button, and a buzz in the Android app
startAppViewport(); // the Android app: a screen height that holds still

// Dev only: ?phone=1 (or a frame named "ar-phone") makes this window behave as
// a touch screen, so a page can be looked at as a phone in a phone-sized frame
// on a computer (/debug/room-frames). Kept in the window's name, so it follows
// the frame from page to page and never leaks into another tab. The JS checks
// for a coarse pointer read true; the rest of a query (orientation, height)
// still applies.
if ((import.meta as any).env?.DEV && typeof window !== "undefined" && window.matchMedia) {
  try {
    if (new URLSearchParams(window.location.search).get("phone") === "1") window.name = "ar-phone";
    if (window.name === "ar-phone") {
      const real = window.matchMedia.bind(window);
      window.matchMedia = (q: string) => real(q.replace(/\(\s*pointer:\s*coarse\s*\)/g, "(min-width: 0px)").replace(/\(\s*hover:\s*none\s*\)/g, "(min-width: 0px)"));
    }
  } catch { /* no storage */ }
}

// New builds apply themselves with a quiet reload, never mid-match. (Not on
// a game site: their servers host the build, there is no service worker.)
if (!ON_PORTAL) startPwaUpdates();

// On a game site the pages are routed in the address's #: the site serves
// one file, not our paths.
const Router = ON_PORTAL ? HashRouter : BrowserRouter;

// (a game site's SDK first: shared/portal.ts)
void startPortal().then(() => {
  const mainContainer = document.getElementById("main");
  createRoot(mainContainer).render(
    <HelmetProvider>
      <AuthProvider>
        {/* the sign-in window a framed game opens: only the sign-in, no app */}
        {isAuthPopup ? <AuthPopup /> : (
          <Router>
            <App />
          </Router>
        )}
      </AuthProvider>
    </HelmetProvider>
  );
  window.setTimeout(portalLoaded, 0);
});
